"use client";

import {NeoModal} from "@/components/@modal/neo.modal";
import {NeoAGgrid} from "@/components/table/neo-table/neo-ag-grid";
import {Button} from "@nextui-org/react";
import type {ApexOptions} from "apexcharts";
import {NextPage} from "next";
import dynamic from "next/dynamic";
import {type ReactNode, useMemo, useState} from "react";
import {
  FiCalendar,
  FiDownload,
  FiTrendingUp,
  FiUsers,
  FiXCircle
} from "react-icons/fi";

const Chart = dynamic(() => import("react-apexcharts"), {ssr: false});

type PaymentMethod = "UPI" | "Card" | "Cash";
type PaymentStatus = "Paid" | "Cancelled";

type PassengerPayment = {
  id: string;
  name: string;
  initials: string;
  avatarColor: string;
  pickupStop: string;
  dropStop: string;
  method: PaymentMethod | "-";
  amount: number;
  paidAt: string;
  paymentDate: string;
  status: PaymentStatus;
};

type TransactionTrip = {
  id: string;
  tripDate: string;
  slot: string;
  routeLabel: string;
  shuttleInfo: string;
  passengers: number;
  usersPaid: number;
  totalUsers: number;
  amountPaid: number;
  lastPayment: string;
  from: string;
  to: string;
  insideStops: string;
  departure: string;
  arrival: string;
  passengersCancelled: number;
  payments: PassengerPayment[];
};

const transactionTrips: TransactionTrip[] = [
  {
    id: "TXN-1001",
    tripDate: "2026-05-07",
    slot: "09:00 AM - 10:30 AM",
    routeLabel: "Singanallur -> NeoSay",
    shuttleInfo: "Shuttle #1Y86 BY 2295 | Route A-12",
    passengers: 24,
    usersPaid: 24,
    totalUsers: 24,
    amountPaid: 8640,
    lastPayment: "10:32 AM",
    from: "Singanallur",
    to: "NeoSay",
    insideStops: "Hope College, Peelamedu, Lakshmi Mills",
    departure: "09:00 AM",
    arrival: "10:00 AM",
    passengersCancelled: 1,
    payments: [
      {
        id: "P1",
        name: "Praveen K",
        initials: "PV",
        avatarColor: "bg-orange-100 text-orange-600",
        pickupStop: "Singanallur",
        dropStop: "NeoSay",
        method: "UPI",
        amount: 360,
        paidAt: "08:41 AM",
        paymentDate: "2026-05-07",
        status: "Paid"
      },
      {
        id: "P2",
        name: "Mithun",
        initials: "MT",
        avatarColor: "bg-blue-100 text-blue-700",
        pickupStop: "Singanallur",
        dropStop: "NeoSay",
        method: "UPI",
        amount: 360,
        paidAt: "08:45 AM",
        paymentDate: "2026-05-07",
        status: "Paid"
      },
      {
        id: "P3",
        name: "Dharun",
        initials: "DR",
        avatarColor: "bg-emerald-100 text-emerald-700",
        pickupStop: "Hopes",
        dropStop: "NeoSay",
        method: "Card",
        amount: 360,
        paidAt: "08:48 AM",
        paymentDate: "2026-05-07",
        status: "Paid"
      },
      {
        id: "P4",
        name: "Megha R",
        initials: "MR",
        avatarColor: "bg-amber-100 text-amber-700",
        pickupStop: "Peelamedu",
        dropStop: "NeoSay",
        method: "Card",
        amount: 300,
        paidAt: "08:52 AM",
        paymentDate: "2026-05-07",
        status: "Paid"
      },
      {
        id: "P5",
        name: "Nivetha D",
        initials: "ND",
        avatarColor: "bg-lime-100 text-lime-700",
        pickupStop: "Lakshmi Mills",
        dropStop: "NeoSay",
        method: "UPI",
        amount: 260,
        paidAt: "08:55 AM",
        paymentDate: "2026-05-07",
        status: "Paid"
      },
      {
        id: "P6",
        name: "Harshath",
        initials: "HS",
        avatarColor: "bg-indigo-100 text-indigo-700",
        pickupStop: "Lakshmi Mills",
        dropStop: "NeoSay",
        method: "-",
        amount: 0,
        paidAt: "-",
        paymentDate: "2026-05-07",
        status: "Cancelled"
      },
      {
        id: "P7",
        name: "Arun",
        initials: "AN",
        avatarColor: "bg-orange-100 text-orange-700",
        pickupStop: "Lakshmi Mills",
        dropStop: "NeoSay",
        method: "-",
        amount: 0,
        paidAt: "-",
        paymentDate: "2026-05-07",
        status: "Cancelled"
      }
    ]
  },
  {
    id: "TXN-1002",
    tripDate: "2026-05-07",
    slot: "09:30 AM - 10:30 AM",
    routeLabel: "Neelambur -> NeoSay",
    shuttleInfo: "Shuttle #1Y86 BY 2295 | Route A-12",
    passengers: 24,
    usersPaid: 24,
    totalUsers: 24,
    amountPaid: 9060,
    lastPayment: "10:34 AM",
    from: "Neelambur",
    to: "NeoSay",
    insideStops: "Chinniyampalayam, Arasur, Lakshmi Nagar",
    departure: "09:30 AM",
    arrival: "10:30 AM",
    passengersCancelled: 0,
    payments: []
  },
  {
    id: "TXN-1003",
    tripDate: "2026-05-08",
    slot: "09:30 AM - 11:30 AM",
    routeLabel: "RS Puram -> NeoSay",
    shuttleInfo: "Shuttle #1Y86 BY 2295 | Route A-12",
    passengers: 22,
    usersPaid: 22,
    totalUsers: 24,
    amountPaid: 7820,
    lastPayment: "11:42 AM",
    from: "RS Puram",
    to: "NeoSay",
    insideStops: "Town Hall, Gandhipuram, Nava India",
    departure: "09:30 AM",
    arrival: "11:30 AM",
    passengersCancelled: 2,
    payments: []
  },
  {
    id: "TXN-1004",
    tripDate: "2026-05-09",
    slot: "06:30 PM - 07:30 PM",
    routeLabel: "NeoSay -> Singanallur",
    shuttleInfo: "Shuttle #1Y86 BY 2295 | Route A-12",
    passengers: 24,
    usersPaid: 24,
    totalUsers: 24,
    amountPaid: 8120,
    lastPayment: "07:31 PM",
    from: "NeoSay",
    to: "Singanallur",
    insideStops: "Lakshmi Mills, Peelamedu, Hope College",
    departure: "06:30 PM",
    arrival: "07:30 PM",
    passengersCancelled: 0,
    payments: []
  },
  {
    id: "TXN-1005",
    tripDate: "2026-05-10",
    slot: "07:00 PM - 08:30 PM",
    routeLabel: "NeoSay -> Neelambur",
    shuttleInfo: "Shuttle #1Y86 BY 2295 | Route A-12",
    passengers: 21,
    usersPaid: 21,
    totalUsers: 24,
    amountPaid: 7540,
    lastPayment: "08:41 PM",
    from: "NeoSay",
    to: "Neelambur",
    insideStops: "Lakshmi Nagar, Arasur, Chinniyampalayam",
    departure: "07:00 PM",
    arrival: "08:30 PM",
    passengersCancelled: 3,
    payments: []
  }
];

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0
  }).format(value);

const formatShuttleTitle = (value: string) =>
  value.replace(/^Shuttle\s+#?1/i, "TN ").trim();

export const Transaction: NextPage<{
  hasDateFilterChanged: boolean;
  searchValue: string;
  filterRequest: {
    from: number | null;
    to: number | null;
  };
}> = ({hasDateFilterChanged, searchValue, filterRequest}) => {
  const [selectedTrip, setSelectedTrip] = useState<TransactionTrip | null>(
    null
  );

  const filteredTrips = useMemo(() => {
    const normalizedQuery = searchValue.trim().toLowerCase();

    return transactionTrips.filter(trip => {
      const matchesSearch =
        !normalizedQuery ||
        [
          trip.id,
          trip.routeLabel,
          trip.shuttleInfo,
          trip.from,
          trip.to,
          trip.slot
        ]
          .filter(Boolean)
          .some(value => value.toLowerCase().includes(normalizedQuery));
      const tripTime = new Date(trip.tripDate).getTime();
      const matchesDate =
        !hasDateFilterChanged ||
        !filterRequest.from ||
        !filterRequest.to ||
        (!Number.isNaN(tripTime) &&
          tripTime >= filterRequest.from &&
          tripTime <= filterRequest.to);

      return matchesSearch && matchesDate;
    });
  }, [filterRequest.from, filterRequest.to, hasDateFilterChanged, searchValue]);
  const exportTripPayments = (trip: TransactionTrip) => {
    const rows = [
      [
        "Passenger",
        "Pickup Stop",
        "Drop Stop",
        "Payment Method",
        "Amount Paid",
        "Paid At",
        "Payment Date",
        "Status"
      ],
      ...trip.payments.map(payment => [
        payment.name,
        payment.pickupStop,
        payment.dropStop,
        payment.method,
        String(payment.amount),
        payment.paidAt,
        payment.paymentDate,
        payment.status
      ])
    ];

    const csv = rows
      .map(columns =>
        columns.map(value => `"${String(value).replace(/"/g, '""')}"`).join(",")
      )
      .join("\n");

    const blob = new Blob([csv], {type: "text/csv;charset=utf-8;"});
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${trip.id.toLowerCase()}-payments.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const summary = useMemo(() => {
    const revenue = filteredTrips.reduce(
      (sum, trip) => sum + trip.amountPaid,
      0
    );
    const tripsCompleted = filteredTrips.length;
    const passengersTravelled = filteredTrips.reduce(
      (sum, trip) => sum + trip.passengers,
      0
    );
    const passengersCancelled = filteredTrips.reduce(
      (sum, trip) => sum + trip.passengersCancelled,
      0
    );

    return {
      revenue,
      tripsCompleted,
      passengersTravelled,
      passengersCancelled
    };
  }, [filteredTrips]);

  const paymentSeries = useMemo(() => {
    const paidPassengers = transactionTrips.flatMap(trip =>
      trip.payments.filter(payment => payment.status === "Paid")
    );

    const totals = paidPassengers.reduce(
      (acc, payment) => {
        if (payment.method === "UPI") {
          acc.upi += payment.amount;
        } else if (payment.method === "Card") {
          acc.card += payment.amount;
        } else if (payment.method === "Cash") {
          acc.cash += payment.amount;
        }
        return acc;
      },
      {upi: 0, card: 0, cash: 0}
    );

    const values = [totals.upi, totals.card, totals.cash];
    return values.some(value => value > 0) ? values : [54, 28, 18];
  }, []);

  const paymentOptions: ApexOptions = {
    chart: {
      type: "donut",
      toolbar: {show: false}
    },
    labels: ["UPI", "Card", "Cash"],
    colors: ["#2563EB", "#22C55E", "#F59E0B"],
    dataLabels: {enabled: false},
    legend: {show: false},
    stroke: {width: 0},
    tooltip: {
      y: {
        formatter: value => `${value.toFixed(0)}`
      }
    },
    plotOptions: {
      pie: {
        donut: {
          size: "70%",
          labels: {
            show: true,
            name: {
              show: true,
              offsetY: 14,
              color: "#64748B",
              fontSize: "11px",
              formatter: () => "Payment"
            },
            value: {
              show: true,
              offsetY: -10,
              color: "#111827",
              fontSize: "14px",
              fontWeight: "700",
              formatter: () => "Method"
            },
            total: {
              show: false
            }
          }
        }
      }
    }
  };

  const trendOptions: ApexOptions = {
    chart: {
      type: "area",
      toolbar: {show: false},
      zoom: {enabled: false}
    },
    colors: ["#2563EB"],
    dataLabels: {enabled: false},
    stroke: {
      curve: "smooth",
      width: 3
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.28,
        opacityTo: 0.02,
        stops: [0, 100]
      }
    },
    grid: {
      borderColor: "#E5E7EB",
      strokeDashArray: 4
    },
    xaxis: {
      categories: [
        "1 Jan",
        "2 Jan",
        "3 Jan",
        "4 Jan",
        "5 Jan",
        "6 Jan",
        "7 Jan"
      ],
      axisBorder: {show: false},
      axisTicks: {show: false},
      labels: {
        style: {colors: "#6B7280", fontSize: "11px"}
      }
    },
    yaxis: {
      min: 0,
      max: 50000,
      tickAmount: 5,
      labels: {
        formatter: value => `${Math.round(value / 1000)}K`,
        style: {colors: "#9CA3AF", fontSize: "11px"}
      }
    },
    tooltip: {
      y: {
        formatter: value => formatCurrency(value)
      }
    },
    legend: {show: false}
  };

  const trendSeries = [
    {
      name: "Revenue",
      data: [32000, 44000, 35000, 38000, 36000, 47000, 45820]
    }
  ];

  return (
    <>
      <div className="flex min-h-[calc(100vh-63px)] w-full flex-col px-4 py-4">
        <div className="grid gap-3 xl:grid-cols-[260px_260px_minmax(0,1fr)]">
          <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
            <div className="text-xs font-semibold text-slate-900">
              Today&apos;s Summary
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2.5">
              <SummaryCard
                label="Revenue"
                value={formatCurrency(summary.revenue)}
                icon={<FiTrendingUp className="h-4 w-4" />}
                iconClass="bg-blue-50 text-blue-600"
              />
              <SummaryCard
                label="Trips Completed"
                value={String(summary.tripsCompleted)}
                icon={<FiCalendar className="h-4 w-4" />}
                iconClass="bg-violet-50 text-violet-600"
              />
              <SummaryCard
                label="Passengers Travelled"
                value={String(summary.passengersTravelled)}
                icon={<FiUsers className="h-4 w-4" />}
                iconClass="bg-amber-50 text-amber-600"
              />
              <SummaryCard
                label="Passengers Cancelled"
                value={String(summary.passengersCancelled)}
                icon={<FiXCircle className="h-4 w-4" />}
                iconClass="bg-rose-50 text-rose-600"
              />
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
            <div className="mt-1.5 flex items-center justify-center">
              <Chart
                type="donut"
                options={paymentOptions}
                series={paymentSeries}
                height={190}
                width="100%"
              />
            </div>
            <div className="mt-1.5 flex justify-center gap-1.5 text-[10px] font-medium">
              <span className="rounded-full bg-blue-50 px-2 py-0.5 text-blue-600">
                UPI 54%
              </span>
              <span className="rounded-full bg-green-50 px-2 py-0.5 text-green-600">
                Card 28%
              </span>
              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-amber-600">
                Cash 18%
              </span>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
            <div className="mt-2">
              <Chart
                type="area"
                options={trendOptions}
                series={trendSeries}
                height={205}
                width="100%"
              />
            </div>
          </div>
        </div>

        <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
          <div className="mt-3">
            <div className="text-base font-semibold text-slate-900">
              Trip payment history
            </div>

            <div className="mt-3 overflow-hidden rounded-2xl border border-slate-100">
              <div className="hidden bg-slate-50 px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400 lg:grid lg:grid-cols-[1.8fr_0.7fr_0.9fr_0.8fr_0.7fr_0.8fr]">
                <div>Trip</div>
                <div>Passengers</div>
                <div>Users paid</div>
                <div>Amount paid</div>
                <div>Last payment</div>
                <div className="text-right">Actions</div>
              </div>
              {filteredTrips.map(trip => (
                <div
                  key={trip.id}
                  className="grid gap-2.5 border-b border-slate-100 px-3 py-3 last:border-b-0 lg:grid-cols-[1.8fr_0.7fr_0.9fr_0.8fr_0.7fr_0.8fr]"
                >
                  <div>
                    <div className="text-xs font-semibold text-slate-900">
                      {trip.slot}
                    </div>
                    <div className="mt-0.5 text-sm text-slate-700">
                      {trip.routeLabel}
                    </div>
                    <div className="text-[11px] text-slate-400">
                      {formatShuttleTitle(trip.shuttleInfo)}
                    </div>
                  </div>

                  <MetricCell value={String(trip.passengers)} />
                  <MetricCell
                    value={`${trip.usersPaid} / ${trip.totalUsers} paid`}
                  />
                  <MetricCell
                    value={formatCurrency(trip.amountPaid)}
                    valueClass="text-emerald-600"
                  />
                  <MetricCell value={trip.lastPayment} />

                  <div className="flex items-center justify-end gap-2">
                    <Button
                      size="sm"
                      variant="bordered"
                      className="h-8 rounded-full border-slate-200 bg-white px-3 text-xs text-slate-600"
                      onPress={() => {
                        setSelectedTrip(trip);
                      }}
                    >
                      View
                    </Button>
                    <Button
                      size="sm"
                      variant="light"
                      className="h-8 rounded-full px-2 text-xs text-slate-600"
                      startContent={<FiDownload className="h-3.5 w-3.5" />}
                      onPress={() => exportTripPayments(trip)}
                    >
                      Export
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <NeoModal
        actionType="transactionDetailView"
        onOpen={Boolean(selectedTrip)}
        onClose={() => setSelectedTrip(null)}
        hasActions={false}
        data={{trip: selectedTrip}}
      />
    </>
  );
};

const SummaryCard = ({
  label,
  value,
  icon,
  iconClass
}: {
  label: string;
  value: string;
  icon: ReactNode;
  iconClass: string;
}) => (
  <div className="rounded-2xl border border-slate-100 p-2.5">
    <div
      className={`flex h-7 w-7 items-center justify-center rounded-lg ${iconClass}`}
    >
      {icon}
    </div>
    <div className="mt-2 text-[11px] text-slate-400">{label}</div>
    <div className="mt-0.5 text-2xl font-semibold tracking-tight text-slate-900">
      {value}
    </div>
  </div>
);

const MetricCell = ({
  value,
  valueClass
}: {
  value: string;
  valueClass?: string;
}) => (
  <div>
    <div className={`text-xs font-semibold text-slate-900 ${valueClass ?? ""}`}>
      {value}
    </div>
  </div>
);
